import { Card, Stack, Typography, Box, useTheme } from '@mui/material';
import { t } from 'i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SearchGrid } from '@/components/SearchGrid';
import { Alert } from '@/components/shared/Alert';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { FONT_WEIGHT, SMBold, MDRegular } from '@/typography/Typography';
import { useEffect, useRef } from 'react';
import {
    useGetPOSLocationsAndDepartments,
    useTipsHistory,
    useTipsHistoryExcelDownload
} from '@/services/paymentsQuery';
import { useTipsFilters } from './useTipsFilters';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { TipsLocationList } from './TipsLocationList';
import {
    TablePagination,
    usePagination
} from '@/components/shared/TablePagination';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import { useMutation } from '@tanstack/react-query';
import PaymentsAPIServiceV2 from '@/services/PaymentsServiceV2';
import {
    PrimaryButton,
    SecondaryButtonRegular,
    OutlinedDangerButton
} from '@/components/shared/Button';
import { downloadExcel } from '@/utils/excelExport';
import { useUser } from '@/contexts/user/UserContext';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const TipsListPage = () => {
    const theme = useTheme();
    const { company } = useUser();
    const {
        locationName,
        statusFilter,
        reportingStatusFilter,
        setLocationName,
        setStatusFilter,
        setReportingStatusFilter,
        filtersKey
    } = useTipsFilters();
    const {
        data,
        isPending: locationPending,
        error: locationError
    } = useGetPOSLocationsAndDepartments();

    useHandleResponse({
        error: locationError
    });
    const { page, resetPage, rowsPerPage, setRowsPerPage } = usePagination();

    // Reset page when filters change
    useEffect(() => {
        resetPage();
    }, [filtersKey]);

    const {
        data: tipsHistoryData,
        isPending: historyPending,
        error: historyError
    } = useTipsHistory(
        page * rowsPerPage,
        rowsPerPage,
        locationName === 'all' ? [] : [locationName],
        statusFilter === 'all' ? [] : [statusFilter],
        reportingStatusFilter === 'all' ? [] : [reportingStatusFilter]
    );

    useHandleResponse({
        error: historyError
    });

    const retryMutation = useMutation({
        mutationFn: () => paymentsService.retryTipsReport(),
        onSuccess: () => {
            // Optionally show success message or refetch data
        },
        onError: (error) => {
            console.error('Failed to retry tips report:', error);
        }
    });

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLoader(locationPending || historyPending));
    }, [locationPending, historyPending]);

    const {
        data: excelData,
        refetch: refetchExcel,
        error: excelError,
        isSuccess: isExcelSuccess,
        isPending: _excelPending
    } = useTipsHistoryExcelDownload(
        locationName === 'all' ? [] : [locationName],
        statusFilter === 'all' ? [] : [statusFilter],
        reportingStatusFilter === 'all' ? [] : [reportingStatusFilter],
        false
    );

    const downloadLock = useRef(true);

    useHandleResponse({
        error: excelError
    });

    useEffect(() => {
        if (isExcelSuccess && !downloadLock.current) {
            downloadLock.current = true;
            const rows = excelData?.data?.tipsHistory ?? [];
            const fileName = `Tips_History_${
                new Date().toISOString().split('T')[0]
            }.xlsx`;
            downloadExcel(rows, 'Tips History', fileName, {
                ignoreFields: ['posJournalAmounts'],
                timezone: company?.ianaTimeZone
            });
        }
    }, [excelData, isExcelSuccess]);

    const resetFilters = () => {
        setLocationName('all');
        setStatusFilter('all');
        setReportingStatusFilter('all');
        resetPage();
    };

    const actions = (
        <Stack direction="row" gap={'8px'}>
            <SecondaryButtonRegular
                onClick={() => {
                    downloadLock.current = false;
                    refetchExcel();
                }}
                size="small"
                sx={{ borderRadius: '5px' }}
            >
                {t('download_as_excel')}
            </SecondaryButtonRegular>
        </Stack>
    );

    return (
        <div>
            <MDRegular sx={{ mb: 3, color: '#667085' }}>
                {t('tips.tips_daily_subtitle')}
            </MDRegular>

            {(tipsHistoryData?.data
                ?.unreportedLocationsInPreviousPayrollsCount ?? 0) > 0 && (
                <Alert
                    severity="error"
                    message={
                        <Box sx={{ color: '#D92D20' }}>
                            <strong>
                                {t('tips.unreported_locations_warning_title', {
                                    count:
                                        tipsHistoryData?.data
                                            ?.unreportedLocationsInPreviousPayrollsCount ??
                                        0
                                })}
                            </strong>{' '}
                            {t('tips.unreported_locations_warning_message')}
                        </Box>
                    }
                    action={
                        <OutlinedDangerButton
                            onClick={() => retryMutation.mutate()}
                            disabled={retryMutation.isPending}
                            size="small"
                            loading={retryMutation.isPending}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {retryMutation.isSuccess ? (
                                <>
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: 16,
                                            mr: 1,
                                            color: 'success.main'
                                        }}
                                    />
                                    {t('request_initiated')}
                                </>
                            ) : (
                                !retryMutation.isPending && t('retry_now')
                            )}
                        </OutlinedDangerButton>
                    }
                />
            )}
            <SearchGrid container justifyContent={'space-between'}>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={1}
                    justifyContent={'space-between'}
                >
                    <div>
                        <SMBold noWrap={true}>{t('filter_by')}</SMBold>
                    </div>
                    <Dropdown
                        style={{ width: '400px' }}
                        choices={[
                            {
                                label: t('tips.show_all_locations'),
                                value: 'all'
                            },
                            ...(data?.data?.locations?.map((filter) => ({
                                label: filter.locationName,
                                value: filter.locationId
                            })) || [])
                        ]}
                        label=""
                        value={locationName}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            setLocationName(val);
                        }}
                    />
                    <Dropdown
                        style={{ width: '250px' }}
                        choices={[
                            {
                                label: t('tips.show_all_statuses'),
                                value: 'all'
                            },
                            {
                                label: t('tips.status_awaiting'),
                                value: 'Awaiting'
                            },
                            {
                                label: t('tips.status_succeeded'),
                                value: 'Succeeded'
                            }
                        ]}
                        label=""
                        value={statusFilter}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            setStatusFilter(val);
                        }}
                    />
                    <Dropdown
                        style={{ width: '250px' }}
                        choices={[
                            {
                                label: t('tips.show_all_reporting_statuses'),
                                value: 'all'
                            },
                            {
                                label: t('tips.reporting_status_not_reported'),
                                value: 'NotReported'
                            },
                            {
                                label: t(
                                    'tips.reporting_status_partially_reported'
                                ),
                                value: 'PartiallyReported'
                            },
                            {
                                label: t(
                                    'tips.reporting_status_fully_reported'
                                ),
                                value: 'FullyReported'
                            },
                            {
                                label: t('tips.check_all_issues'),
                                value: 'AllIssues',
                                labelStyle: { color: theme.palette.error.main },
                                itemStyle: { color: theme.palette.error.main }
                            }
                        ]}
                        label=""
                        value={reportingStatusFilter}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            setReportingStatusFilter(val);
                        }}
                    />
                    <PrimaryButton onClick={resetFilters}>
                        <Typography
                            fontSize={'14px'}
                            lineHeight={'20px'}
                            fontWeight={600}
                            noWrap
                        >
                            {t('reset_filter')}
                        </Typography>
                    </PrimaryButton>
                </Stack>
            </SearchGrid>

            <Card>
                <CardHeader
                    label={t('tips.daily_settlements')}
                    chip={
                        <Chip
                            icon={<span></span>}
                            label={
                                <Typography fontWeight={FONT_WEIGHT.SEMI_BOLD}>
                                    {t('filtered_of_total', {
                                        filtered:
                                            tipsHistoryData?.data
                                                ?.filteredTotal ?? 0,
                                        total: tipsHistoryData?.data?.total ?? 0
                                    })}
                                </Typography>
                            }
                            color={'success'}
                        />
                    }
                    actions={actions}
                />
                <TipsLocationList
                    items={tipsHistoryData?.data?.tipsHistory}
                    reloadTransactions={() => {}}
                ></TipsLocationList>
                {(tipsHistoryData?.data?.tipsHistory?.length ?? 0) == 0 && (
                    <Typography textAlign={'center'}>
                        {t('no_records')}
                    </Typography>
                )}
                <TablePagination
                    page={page}
                    rowsPerPageOptions={[
                        {
                            label: '16',
                            value: 16
                        },
                        {
                            label: '24',
                            value: 24
                        },
                        {
                            label: '32',
                            value: 32
                        },
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                    count={tipsHistoryData?.data?.total ?? 0}
                    rowsPerPage={rowsPerPage}
                    resetPage={resetPage}
                    onPageChange={() => {}}
                    onRowsPerPageChange={(newRowsPerPage) => {
                        setRowsPerPage(newRowsPerPage as number);
                    }}
                />
            </Card>
        </div>
    );
};
